import React from 'react'
import PropTypes from 'prop-types'
import { Radio, Input } from 'components/UIElements'
import GroupedSelect from '../shared/GroupedSelect'
import {
  DURATION_UNIT,
  DURATION_UNIT_OPTIONS,
  SAVE_RESUME_MODE,
  SAVE_RESUME_OPTIONS,
  EDIT_AFTER_SUBMISSION_MODE,
  EDIT_AFTER_SUBMISSION_OPTIONS,
} from '../../constants'
import '../../styles/schedulingEngine.scss'

const readSaveResume = value => {
  const save = value?.saveResume || {}
  return {
    mode: save.mode || SAVE_RESUME_MODE.no,
    value: save.value ?? save.minutes ?? 30,
    unit: save.unit || DURATION_UNIT.minutes,
  }
}

const readEditAfter = value => {
  const edit = value?.editAfterSubmission || {}
  return {
    mode: edit.mode || EDIT_AFTER_SUBMISSION_MODE.no,
    value: edit.value ?? edit.hours ?? 24,
    unit: edit.unit || DURATION_UNIT.hours,
  }
}

const NumberWithUnit = ({ value, unit, disabled, min = 1, onChange, onUnitChange, onActivate }) => (
  <span className='se-number-unit'>
    <Input
      inline
      type='number'
      min={min}
      value={value}
      disabled={disabled}
      onFocus={onActivate}
      onChange={next => {
        onActivate()
        onChange(next)
      }}
    />
    <GroupedSelect
      className='se-field-inline se-duration-unit'
      label=''
      ariaLabel='Duration unit'
      value={unit}
      options={DURATION_UNIT_OPTIONS}
      disabled={disabled}
      onChange={onUnitChange}
    />
  </span>
)

NumberWithUnit.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  onChange: PropTypes.func,
  onUnitChange: PropTypes.func,
  onActivate: PropTypes.func,
}

const ParticipantSessionProperties = ({ value, onChange, disabled }) => {
  const saveResume = readSaveResume(value)
  const editAfter = readEditAfter(value)
  const setSaveResume = patch =>
    onChange({
      ...value,
      saveResume: { ...saveResume, ...patch },
    })
  const setEditAfter = patch =>
    onChange({
      ...value,
      editAfterSubmission: { ...editAfter, ...patch },
    })

  return (
    <div className='se-permissions'>
      <div className='se-permissions-fields'>
        <h4>Save and resume</h4>
        <Radio
          id='save-resume-no'
          selected={saveResume.mode === SAVE_RESUME_MODE.no}
          disabled={disabled}
          content={SAVE_RESUME_OPTIONS[0].text}
          onClick={() => setSaveResume({ mode: SAVE_RESUME_MODE.no })}
        />
        <Radio
          id='save-resume-yes'
          selected={saveResume.mode === SAVE_RESUME_MODE.yesMinutes}
          disabled={disabled}
          content={
            <span className='se-radio-inline'>
              {SAVE_RESUME_OPTIONS[1].text}
              <NumberWithUnit
                value={saveResume.value}
                unit={saveResume.unit}
                disabled={disabled}
                onActivate={() => setSaveResume({ mode: SAVE_RESUME_MODE.yesMinutes })}
                onChange={next => setSaveResume({ mode: SAVE_RESUME_MODE.yesMinutes, value: next })}
                onUnitChange={unit => setSaveResume({ mode: SAVE_RESUME_MODE.yesMinutes, unit })}
              />
            </span>
          }
          onClick={() => setSaveResume({ mode: SAVE_RESUME_MODE.yesMinutes })}
        />

        <h4>Edit after submission</h4>
        <Radio
          id='edit-after-no'
          selected={editAfter.mode === EDIT_AFTER_SUBMISSION_MODE.no}
          disabled={disabled}
          content={EDIT_AFTER_SUBMISSION_OPTIONS[0].text}
          onClick={() => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.no })}
        />
        <Radio
          id='edit-after-hours'
          selected={editAfter.mode === EDIT_AFTER_SUBMISSION_MODE.hours}
          disabled={disabled}
          content={
            <span className='se-radio-inline'>
              {EDIT_AFTER_SUBMISSION_OPTIONS[1].text}
              <NumberWithUnit
                value={editAfter.value}
                unit={editAfter.unit}
                disabled={disabled}
                onActivate={() => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.hours })}
                onChange={next => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.hours, value: next })}
                onUnitChange={unit => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.hours, unit })}
              />
              after submission
            </span>
          }
          onClick={() => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.hours })}
        />
        <Radio
          id='edit-after-expires'
          selected={editAfter.mode === EDIT_AFTER_SUBMISSION_MODE.untilExpires}
          disabled={disabled}
          content={EDIT_AFTER_SUBMISSION_OPTIONS[2].text}
          onClick={() => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.untilExpires })}
        />
        <Radio
          id='edit-after-none'
          selected={editAfter.mode === EDIT_AFTER_SUBMISSION_MODE.noLimit}
          disabled={disabled}
          content={EDIT_AFTER_SUBMISSION_OPTIONS[3].text}
          onClick={() => setEditAfter({ mode: EDIT_AFTER_SUBMISSION_MODE.noLimit })}
        />
      </div>
      <aside className='se-permissions-help'>
        <p>
          Saving and resuming covers an unfinished attempt. Editing after submission covers a completed one — they are
          separate permissions and can be set independently.
        </p>
        <p>
          The edit window is counted from the participant&apos;s submission, not from the instance date, so a late
          submission still gets its full window.
        </p>
      </aside>
    </div>
  )
}

ParticipantSessionProperties.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
}

export default ParticipantSessionProperties
